/**
 * SectionGrid — renders a contract `Layout` as a CSS Grid.
 *
 * Places each `Section` by its row/column/rowSpan/columnSpan, and dispatches by
 * `section.kind`: `chartGroup` → the live SciChart chart; `screener` → the grid
 * island; everything else → a typed placeholder panel.
 *
 * ## Contract tracks vs the inserted column
 *
 * The grid can carry one **inserted column** that is not contract data — today
 * the docked screener editor. It is ephemeral view state (D8): opening an editor
 * must not mutate the saved layout, or reloading a workspace would reopen
 * whatever panel happened to be showing when it was saved.
 *
 * Everything therefore works on an *effective* track list — contract columns
 * with the inserted one spliced in. Sections at or past the insertion shift one
 * track right, dividers are computed over the effective list, and a drag splits
 * its result back into contract `columnSizes` and the inserted size. Keeping one
 * effective list rather than two coordinate systems is what stops a divider from
 * drifting off the seam it belongs to.
 */

import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type {
  Annotation,
  BriefBoardConfig,
  ChartGroup,
  DockPanel,
  Layout,
  Pane,
  ScreenerConfig,
  Section,
} from "@qufolio/model";
import type { ScreenerConfigPatch, ScreenerTimings } from "@qufolio/screener-grid";
import type { ScreenerColumnMeta, ScreenerField } from "@qufolio/transport";
import { ChartGroupView } from "./ChartGroupView";
import type { AnnotationTool } from "./AnnotationTools";
import type { LegendControls } from "./ValueLegend";
import { ScreenerView } from "./ScreenerView";
import { SeasonalityView } from "./SeasonalityView";
import { TopDownView } from "./TopDownView";
import { DockCell } from "./DockCell";
import { BucketsBriefView } from "./BucketsBriefView";
import { bucketsBriefConfig } from "../lib/buckets-brief";
import { BriefBoardView } from "./BriefBoardView";
import { briefBoardConfig, isMemberCopy } from "../lib/brief-board";
import { chartGroupConfig, screenerConfig } from "../lib/section-config";
import {
  dragMacroScale,
  isMacroKind,
  isScalableMacroKind,
  isStackedMacroLayout,
  MACRO_KIND_TITLE,
  macroScale,
  stackedRows,
  withMacroScale,
  type MacroKind,
  type MacroSectionConfig,
} from "../lib/macro-sections";
import { MacroSectionHost } from "./macro/MacroSectionHost";
import { MacroNote } from "./macro/MacroNote";
import { isNoteKind } from "../api/macro-notes";
import type { ChartJump } from "./macro/types";
import { MacroCursorProvider } from "../lib/macro-cursor";
import type { PipPatch } from "../lib/pip";
import { activeRingStyle, claimsActivation } from "../lib/active-section";
import { seamRuns, type GridCell } from "../lib/grid-seams";
import { crosshairLinked } from "../lib/crosshair-link";
import { chartsOf, screenerPlacement } from "../lib/layout-templates";

/**
 * The corner radius every panel in the grid shares.
 *
 * Small on purpose. The seams here are a 1px `gap` with `--qf-border` showing
 * through, so a large radius would open a visible notch of border colour at
 * every junction where four panels meet — the rounding has to stay under what
 * the eye reads as a corner or it becomes a hole.
 *
 * One constant rather than a value per panel, because the chart cells, the
 * screener, the selection ring and the toolbar strip all meet along the same
 * seams: a radius that differs between two of them reads as a misalignment
 * rather than as a style.
 */
const PANEL_RADIUS = 6;

/**
 * What a screener section can write back.
 *
 * Wider than the grid's own `ScreenerConfigPatch` by `savedFilters`: the grid
 * emits view state, while the filter *library* is edited through the builder and
 * has to be persisted too. (§1 of the DSL plan moves the library to
 * `UserWorkspace`; until then it lives on the section.)
 */
export type ScreenerPatch = ScreenerConfigPatch &
  Partial<Pick<ScreenerConfig, "savedFilters">>;

/** A screener's decoded result schema, forwarded to a docked editor. */
export interface ScreenerSchema {
  fields: ScreenerField[];
  meta: ScreenerColumnMeta[];
  /** What the last response cost, per stage — the editor's `i` panel. */
  timings?: ScreenerTimings;
}
const MIN_FR = 0.15;

/** An ephemeral column spliced into the grid, and the toolbar that toggles it. */
export interface InsertedColumn {
  /**
   * Contract column index it is placed *before*.
   *
   * Before rather than after, because the editor now docks on either side of
   * the chart region — "before column 0" is the only way to say leftmost, and
   * `after: -1` is not a column index anybody should have to reason about.
   */
  at: number;
  /** Track size, an `fr` string — so it shares the dividers' arithmetic. */
  size: string;
  onSize: (size: string) => void;
  children: ReactNode;
}

export interface GridToolbar {
  /** Contract column the strip hugs — the chart region's edge. */
  column: number;
  /** Which edge of that column the strip's own edge lands on. */
  side: "left" | "right";
  /**
   * Contract rows the strip runs down, half-open.
   *
   * The chart region's with the screener on the left or right, which is every
   * row anyway. With it docked top or bottom, the whole grid — and `parts` says
   * which keys sit beside which rows (`toolbarAnchor`, owner 2026-10-07).
   */
  rows: [number, number];
  /** Fixed pixel width; the strip does not scale with the tracks. */
  width: number;
  /** The strip's contents when it is one part — ignored when `parts` is given. */
  children?: ReactNode;
  /**
   * The strip in parts, each beside its own rows: the dock keys beside the
   * screener, the chart keys beside the charts. Still one strip on screen; the
   * parts are positioned from the same row shares the grid uses, so the seam
   * between them is the row divider and follows its drag.
   */
  parts?: readonly ToolbarPart[];
  /**
   * A panel pinned open beside the keys (`Dock.pinned`, owner 2026-10-26): the
   * saved conditions, the strip's full height, on the charts' side of the keys.
   *
   * Part of the strip rather than a grid track, so it goes wherever the strip
   * goes — the seam beside a right-hand screener, or the full-height left edge
   * with the screener on top — and the cells beside it give up its width
   * through the same padding they give the keys.
   */
  pinned?: { width: number; children: ReactNode };
}

/** One part of a split strip: its contents, and the contract rows beside them. */
export interface ToolbarPart {
  rows: [number, number];
  children: ReactNode;
}

/** The dock as the grid needs it: `lib/dock.ts` resolved against one layout. */
export interface DockView {
  /** What shows, first to last; `screener` is built by the grid itself. */
  panels: DockPanel[];
  /** Every panel except the screener, by kind. */
  nodes: Partial<Record<DockPanel, ReactNode>>;
  sizes: string[];
  direction: "row" | "column";
  onSizes: (sizes: string[]) => void;
  onSwap: () => void;
}

export interface SectionGridProps {
  layout: Layout;
  /** The armed drawing tool and the one chart it applies to. */
  drawing?: { chartGroupId: string; tool: AnnotationTool | null; trading?: string | null };
  onAnnotationDrawn?: (chartGroupId: string, paneId: string, annotation: Annotation) => void;
  /** The journal's lines, and the one chart whose symbol they belong to. */
  journal?: {
    chartGroupId: string;
    annotations: readonly Annotation[];
    editable?: ReadonlySet<string>;
  };
  onLevelMoved?: (annotationId: string, price: number) => void;
  onLevelMenu?: (annotationId: string, x: number, y: number) => void;
  /**
   * The value legend's controls for one chart.
   *
   * A function of the group rather than a value, because every one of them
   * addresses a plot in *that* chart — a single object would give the second
   * chart on a layout the first one's rows.
   */
  legendControlsFor?: (group: ChartGroup) => LegendControls | undefined;
  onPlacing?: (placing: boolean) => void;
  onAnnotationsDeleted?: (chartGroupId: string, annotationIds: readonly string[]) => void;
  onAnnotationStyled?: (
    chartGroupId: string,
    annotationId: string,
    style: NonNullable<Annotation["style"]>,
  ) => void;
  /** Bumped to clear the drawings the contract cannot reach. */
  onPlanDrawn?: (chartGroupId: string, entry: number, stop: number, at: number) => void;
  clearNonce?: number;
  onLastPrice?: (symbol: string, price: number, at: number) => void;
  onColumnSizes: (sizes: string[]) => void;
  onRowSizes: (sizes: string[]) => void;
  /**
   * Rendered above the chart, **inside its own column**.
   *
   * Symbol and frequency describe one section, so spanning them across the whole
   * layout put them over a screener they have nothing to do with — and cost
   * every section a row of height to say it. Inside the column, the screener
   * starts level with them and gets that row back.
   */
  /**
   * The header for one chart section, or nothing.
   *
   * A function of the section rather than a single node: with more than one
   * chart the same node would render above every one of them, so the caller
   * decides which section gets a toolbar — today, the active one.
   */
  chartHeader?: (section: Section) => React.ReactNode;
  /**
   * Draws a `dataView` cell — one data panel view (`lib/data-layout.ts`). The
   * caller's, because which chart a cell follows is the workspace's business,
   * not the grid's.
   */
  renderDataView?: (section: Section) => React.ReactNode;
  /** The section the toolbar and the screener currently target. */
  activeSectionId?: string;
  /** A section was pressed. */
  onActivate?: (sectionId: string) => void;
  /** A screener section's grid state changed; persist it into the workspace. */
  onScreenerChange?: (sectionId: string, patch: ScreenerPatch) => void;
  /** A Daily Brief's board changed — its whole config, preset and `mine` together. */
  onSectionConfig?: (sectionId: string, config: BriefBoardConfig) => void;
  /** A macro section's settings changed — its whole config (`lib/macro-sections.ts`). */
  onMacroSectionConfig?: (sectionId: string, config: MacroSectionConfig) => void;
  /** A macro section's "chart this in that layout" — the calendar's symbol click. */
  chartJump?: ChartJump;
  /**
   * Acting as a group's own account (`/auth/me`'s `publishTo`). A Daily Brief
   * on a copied layout edits the preset then, and the member's own board
   * otherwise (`isMemberCopy`).
   */
  authoring?: boolean;
  /** Pane heights settled after a chart divider drag. */
  onPaneHeights?: (chartGroupId: string, heights: string[]) => void;
  /** A chart's panes were restacked, arriving as the finished array. */
  onPaneOrder?: (chartGroupId: string, panes: Pane[]) => void;
  /** Which company events each chart marks. View state today (D8). */
  eventsFor?: (chartGroupId: string) => { earnings: boolean; news: boolean };
  /** One edit to a chart's picture-in-picture panel. */
  onPip?: (chartGroupId: string, patch: PipPatch) => void;
  /** Whether this chart should draw its PIP — the two-chart rule. */
  pipDrawn?: (chartGroupId: string) => boolean;
  /** A chart's span preset chose a new window, and possibly a bigger load. */
  onChartWindow?: (
    chartGroupId: string,
    window: { visibleBars?: number; barsRange?: number },
  ) => void;
  /**
   * A date to centre one chart on — transient, never workspace data (D8).
   *
   * Threaded down rather than resolved above because the bar timestamps live
   * with the chart that loaded them, and a category axis needs the *index* of a
   * date. The alternative was publishing five thousand timestamps upwards on
   * every load so the toolbar could search them, which moves a lot of data to
   * avoid one prop.
   */
  goTo?: { chartGroupId: string; date: string; nonce: number };
  /** A screener published its decoded schema — the docked editor needs it. */
  onScreenerSchema?: (sectionId: string, schema: ScreenerSchema) => void;
  /** A screener row was picked — the symbol to chart. */
  onSelectSymbol?: (symbol: string) => void;
  /**
   * Hide the screener — offered by the screener's own group bar.
   *
   * The control lives on the thing being hidden because that is where it is
   * discoverable: the previous home was a tick in right-click → Settings, which
   * nobody opens to make a panel go away. It is *view* state (D8), so this only
   * ever reaches `App`'s `screenerShown` and never the workspace.
   */
  onHideScreener?: () => void;
  /** ＋ New screen, from the screener bar's screen list. */
  onNewScreen?: () => void;
  /** ✎ Edit this screen, from the screener bar's screen list. */
  onEditScreen?: (via?: "columns") => void;
  /**
   * What the screener's cell shows: the **dock** (`docs/plans/split-view.md`).
   *
   * One or two panels, from `Layout.dock`. The screener is built here, because
   * it is this grid's own section; every other panel arrives as a node in
   * `nodes`, which keeps this file unaware that a data panel exists.
   *
   * **A panel not in `panels` is not rendered at all**, the screener included —
   * see `SectionView`. Absent means the screener alone, as before the dock.
   */
  dock?: DockView;
  insertedColumn?: InsertedColumn;
  toolbar?: GridToolbar;
  /**
   * Right-click on the workspace surface. Handled here rather than per section
   * so the gesture works over a chart, a screener and the gaps between them
   * alike — the menu belongs to the workspace, not to any one widget.
   */
  onContextMenu?: (e: ReactMouseEvent) => void;
}

/** Parse an array of CSS `fr` size strings into numbers, defaulting to 1fr each. */
function parseFrSizes(sizes: string[] | undefined, count: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    const raw = sizes?.[i];
    const n = raw ? parseFloat(raw) : NaN;
    out.push(Number.isFinite(n) && n > 0 ? n : 1);
  }
  return out;
}

const toFrStrings = (fr: number[]): string[] => fr.map((f) => `${f.toFixed(3)}fr`);

/**
 * Track sizes for `grid-template-*`.
 *
 * `1fr` means `minmax(auto, 1fr)`, so a track can never shrink below its
 * content's min-content size. A section whose content is intrinsically large —
 * a data grid, say — then forces its track wider or taller than its share,
 * stealing space from its neighbours and overflowing the container. Pinning the
 * minimum to 0 makes the tracks honour their `fr` ratios and lets each section
 * scroll internally instead.
 */
const toTrackSizes = (fr: number[]): string =>
  fr.map((f) => `minmax(0, ${f.toFixed(3)}fr)`).join(" ");

const sum = (xs: number[], end: number): number =>
  xs.slice(0, end).reduce((a, b) => a + b, 0);

const clamp = (v: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, v));

export function SectionGrid({
  layout,
  onColumnSizes,
  onRowSizes,
  chartHeader,
  renderDataView,
  activeSectionId,
  onActivate,
  onScreenerChange,
  onSectionConfig,
  onMacroSectionConfig,
  chartJump,
  authoring = false,
  onPaneHeights,
  onPaneOrder,
  eventsFor,
  onPip,
  pipDrawn,
  onChartWindow,
  goTo,
  drawing,
  onAnnotationDrawn,
  journal,
  onLevelMoved,
  onLevelMenu,
  legendControlsFor,
  onPlacing,
  onAnnotationsDeleted,
  onAnnotationStyled,
  onPlanDrawn,
  clearNonce,
  onLastPrice,
  onScreenerSchema,
  onSelectSymbol,
  onHideScreener,
  onNewScreen,
  onEditScreen,
  dock,
  insertedColumn,
  toolbar,
  onContextMenu,
}: SectionGridProps) {
  // Derived from the geometry, never stored (`screenerPlacement`'s rule): the
  // screener bar stacks when docked left or right. Only asked when there are
  // charts to be beside: `screenerPlacement` answers "right" for a layout that
  // is all screener, which would stack a bar that has the whole width.
  const screenerSide =
    chartsOf(layout).length > 0 && layout.sections.some((s) => s.kind === "screener")
      ? screenerPlacement(layout)
      : undefined;
  const gridRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  // Tracked apart from hover because the pointer is captured during a drag: it
  // can leave the 9px hit area while the seam is still moving, and the line
  // should stay lit until release.
  const [dragging, setDragging] = useState<string | null>(null);
  // A stacked Macro section's bottom edge mid-drag: the live scale, drawn from
  // local state and written once on release (one config write, one undo).
  const [liveScale, setLiveScale] = useState<{ id: string; scale: number } | null>(null);

  const contractFr = parseFrSizes(layout.columnSizes, layout.columns);
  const rowFr = parseFrSizes(layout.rowSizes, layout.rows);

  // Effective columns: the contract's, with the ephemeral one spliced in.
  const insertAt = insertedColumn ? insertedColumn.at : -1;
  const colFr = insertedColumn
    ? [
        ...contractFr.slice(0, insertAt),
        parseFrSizes([insertedColumn.size], 1)[0]!,
        ...contractFr.slice(insertAt),
      ]
    : contractFr;

  const colTotal = colFr.reduce((a, b) => a + b, 0);
  const rowTotal = rowFr.reduce((a, b) => a + b, 0);

  /** Split an effective track list back into its contract and inserted parts. */
  const commitColumns = (effective: number[]) => {
    if (!insertedColumn) {
      onColumnSizes(toFrStrings(effective));
      return;
    }
    const contract = effective.filter((_, i) => i !== insertAt);
    onColumnSizes(toFrStrings(contract));
    insertedColumn.onSize(`${effective[insertAt]!.toFixed(3)}fr`);
  };

  /**
   * Drag a divider between two adjacent tracks. `boundary` is the index of the
   * track on the left/top of the divider; its neighbour is `boundary + 1`. The
   * pair's combined size is preserved while the split point follows the pointer.
   */
  const startDrag = (
    e: ReactPointerEvent,
    axis: "col" | "row",
    boundary: number,
  ) => {
    e.preventDefault();
    const container = gridRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const fr = axis === "col" ? [...colFr] : [...rowFr];
    const total = fr.reduce((a, b) => a + b, 0);
    const before = sum(fr, boundary);
    const pairTotal = (fr[boundary] ?? 1) + (fr[boundary + 1] ?? 1);
    setDragging(`${axis}-${boundary}`);

    const onMove = (ev: PointerEvent) => {
      const frac =
        axis === "col"
          ? (ev.clientX - rect.left) / rect.width
          : (ev.clientY - rect.top) / rect.height;
      const leadFr = clamp(frac * total - before, MIN_FR, pairTotal - MIN_FR);
      const next = [...fr];
      next[boundary] = leadFr;
      next[boundary + 1] = pairTotal - leadFr;
      if (axis === "col") commitColumns(next);
      else onRowSizes(toFrStrings(next));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setDragging(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  /** Effective (post-insertion) 0-based column a section starts at. */
  const effectiveColumnOf = (section: Section): number =>
    section.column + (insertedColumn && section.column >= insertAt ? 1 : 0);

  /** Grid column for a section, shifted past the inserted track. */
  const gridColumnOf = (section: Section): string =>
    `${effectiveColumnOf(section) + 1} / span ${section.columnSpan ?? 1}`;

  /** The strip's own column, once the ephemeral track has shifted things right. */
  const toolbarColumn = toolbar
    ? toolbar.column + (insertedColumn && toolbar.column >= insertAt ? 1 : 0)
    : 0;

  /** The strip's whole width: its keys plus a pinned panel. */
  const toolbarWidth = toolbar ? toolbar.width + (toolbar.pinned?.width ?? 0) : 0;

  /**
   * Does this section's edge sit under the toolbar strip?
   *
   * Both axes have to agree. With the screener on the left or right the strip
   * runs down the chart region's rows only; docked top or bottom it runs the
   * whole grid, so the screener sharing the strip's column is behind it too and
   * gives up the same width as the charts.
   */
  const abutsToolbar = (section: Section): boolean => {
    if (!toolbar) return false;
    const [rowFrom, rowTo] = [section.row, section.row + (section.rowSpan ?? 1)];
    if (rowTo <= toolbar.rows[0] || rowFrom >= toolbar.rows[1]) return false;
    const start = effectiveColumnOf(section);
    return toolbar.side === "right"
      ? start + (section.columnSpan ?? 1) - 1 === toolbarColumn
      : start === toolbarColumn;
  };

  /** The seam the strip's own edge lands on, as a percentage of the width. */
  const toolbarEdgePct = toolbar
    ? (sum(colFr, toolbar.side === "right" ? toolbarColumn + 1 : toolbarColumn) / colTotal) * 100
    : 0;

  // Dividers are drawn only where they separate two cells (`lib/grid-seams`),
  // so a screener spanning every column is not crossed by the charts' seams.
  // The inserted column spans one track, so it can never straddle a boundary.
  const colCells: GridCell[] = layout.sections.map((section) => ({
    start: effectiveColumnOf(section),
    span: section.columnSpan ?? 1,
    crossStart: section.row,
    crossSpan: section.rowSpan ?? 1,
  }));
  const rowCells: GridCell[] = colCells.map((c) => ({
    start: c.crossStart,
    span: c.crossSpan,
    crossStart: c.start,
    crossSpan: c.span,
  }));

  // One macro cursor per grid (`lib/macro-cursor.ts`). Deliberately not keyed
  // on the layout: a key would remount every chart on a tab switch. The
  // yieldCurve section clears the cursor as it unmounts instead.
  // A layout of Macro sections only (two or more) is a page, not a grid
  // (owner, 2026-11-01): each section at its own height, stacked, and one
  // scrollbar for the whole layout. A lone section — the Calendar layout —
  // keeps the grid so it fills the height.
  const stack = isStackedMacroLayout(layout);
  return (
    <MacroCursorProvider>
    <div
      ref={gridRef}
      onContextMenu={onContextMenu}
      data-stacked={stack || undefined}
      style={
        stack
          ? {
              position: "relative",
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              gap: 6,
              overflowY: "auto",
              background: "var(--qf-border)",
            }
          : {
              position: "relative",
              flex: 1,
              minHeight: 0,
              display: "grid",
              gridTemplateColumns: toTrackSizes(colFr),
              gridTemplateRows: toTrackSizes(rowFr),
              gap: 1,
              background: "var(--qf-border)",
            }
      }
    >
      {(() => {
      const cell = (section: Section, place: React.CSSProperties) => {
        // Mid-drag, the section draws at the live scale; the stored config is
        // untouched until release.
        const shown =
          stack && liveScale?.id === section.id && isScalableMacroKind(section.kind)
            ? { ...section, config: { ...(section.config as object), scale: liveScale.scale } }
            : section;
        return (
        <SectionCell
          key={section.id}
          sectionId={section.id}
          // Only a chart claims it — see `claimsActivation`. A screener click
          // used to activate the screener, which the resolver then absorbed by
          // falling back to the first chart, silently moving the toolbar and
          // the ring off whichever chart the user was actually using.
          onActivate={claimsActivation(section) ? onActivate : undefined}
          // Which cell is selected, and what the border is made of, is
          // `lib/active-section`'s — the module that already knows when a ring
          // is meaningful at all, and the only place the accent is named.
          ring={activeRingStyle(layout.sections, section.id, activeSectionId)}
          style={{
            ...place,
            minWidth: 0,
            borderRadius: PANEL_RADIUS,
            background: "var(--qf-background)",
            // The toolbar floats over one of this cell's edges, so the cell
            // gives up that strip. Padding rather than an inset threaded down to
            // each renderer: it shrinks the content box, so the chart canvas
            // *and* its pane dividers stop at the toolbar without either knowing
            // the toolbar exists.
            ...(abutsToolbar(section)
              ? toolbar!.side === "right"
                ? { paddingRight: toolbarWidth }
                : { paddingLeft: toolbarWidth }
              : {}),
          }}
        >
          <SectionView
            section={shown}
            layoutId={layout.id}
            windowLink={layout.sync?.barRange ? layout.id : undefined}
            crosshairLink={crosshairLinked(layout.sync) ? layout.id : undefined}
            onScreenerChange={onScreenerChange}
            onSectionConfig={onSectionConfig}
            onMacroSectionConfig={onMacroSectionConfig}
            chartJump={chartJump}
            briefMember={isMemberCopy(layout, authoring)}
            sourceVersion={layout.source?.version}
            onPaneHeights={onPaneHeights}
            onPaneOrder={onPaneOrder}
            eventsFor={eventsFor}
            onPip={onPip}
            pipDrawn={pipDrawn}
            onChartWindow={onChartWindow}
            goTo={goTo}
            drawing={drawing}
            onAnnotationDrawn={onAnnotationDrawn}
            journal={journal}
            onLevelMoved={onLevelMoved}
            onLevelMenu={onLevelMenu}
            legendControlsFor={legendControlsFor}
            onPlacing={onPlacing}
            onAnnotationsDeleted={onAnnotationsDeleted}
            onAnnotationStyled={onAnnotationStyled}
            onPlanDrawn={onPlanDrawn}
            clearNonce={clearNonce}
            onLastPrice={onLastPrice}
            onScreenerSchema={onScreenerSchema}
            onSelectSymbol={onSelectSymbol}
            onHideScreener={onHideScreener}
            onNewScreen={onNewScreen}
            onEditScreen={onEditScreen}
            screenerSide={screenerSide}
            dock={dock}
            chartHeader={chartHeader?.(section)}
            dataView={section.kind === "dataView" ? renderDataView?.(section) : undefined}
          />
          {stack && isScalableMacroKind(section.kind) && onMacroSectionConfig && (
            <MacroResizeHandle
              title={section.title ?? MACRO_KIND_TITLE[section.kind]}
              scale={macroScale(section.config as { scale?: number })}
              onLive={(scale) => setLiveScale(scale == null ? null : { id: section.id, scale })}
              onCommit={(scale) =>
                onMacroSectionConfig(
                  section.id,
                  withMacroScale((section.config ?? {}) as { scale?: number }, scale) as MacroSectionConfig,
                )
              }
            />
          )}
        </SectionCell>
        );
      };
      if (!stack)
        return layout.sections.map((section) =>
          cell(section, {
            gridColumn: gridColumnOf(section),
            gridRow: `${section.row + 1} / span ${section.rowSpan ?? 1}`,
            minHeight: 0,
            overflow: "hidden",
          }),
        );
      // Stacked (owner, 2026-11-02): a section alone on its row is full width;
      // sections sharing a row sit side by side, by column, each an equal
      // flexible share at its own natural height — `flex-start`, so each
      // bottom edge (and its resize handle) is that section's own.
      // The AI's overall note leads a Macro page (docs/plans/macro-commentary.md):
      // everyone's, as the sections are, and only where a section has a note.
      const overall =
        layout.sections.some((s) => isNoteKind(s.kind)) ? (
          <div
            key="macro-overall-note"
            data-macro-overall
            style={{ flex: "0 0 auto", borderRadius: PANEL_RADIUS, background: "var(--qf-background)", padding: "2px 0" }}
          >
            <MacroNote kind="overall" />
          </div>
        ) : null;
      return [overall, ...stackedRows(layout.sections).map((row) =>
        row.length === 1 ? (
          cell(row[0]!, { flex: "0 0 auto" })
        ) : (
          <div
            key={`row-${row[0]!.row}`}
            data-macro-row={row[0]!.row}
            style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", gap: 6, flex: "0 0 auto" }}
          >
            {row.map((section) => cell(section, { flex: "1 1 0", minWidth: 0 }))}
          </div>
        ),
      )];
      })()}

      {insertedColumn && (
        <div
          style={{
            gridColumn: `${insertAt + 1} / span 1`,
            gridRow: `1 / span ${layout.rows}`,
            minWidth: 0,
            minHeight: 0,
            overflow: "hidden",
            borderRadius: PANEL_RADIUS,
            background: "var(--qf-background)",
          }}
        >
          {insertedColumn.children}
        </div>
      )}

      {/* Column dividers (between interior columns). */}
      {colFr.slice(0, -1).flatMap((_, i) => {
        const leftPct = (sum(colFr, i + 1) / colTotal) * 100;
        const key = `col-${i}`;
        const lit = hovered === key || dragging === key;
        return seamRuns(colCells, i, rowFr.length).map((run) => (
          <div
            key={`${key}-${run.from}`}
            onPointerDown={(e) => startDrag(e, "col", i)}
            onPointerEnter={() => setHovered(key)}
            onPointerLeave={() => setHovered((h) => (h === key ? null : h))}
            style={{
              position: "absolute",
              top: `${(sum(rowFr, run.from) / rowTotal) * 100}%`,
              bottom: `${100 - (sum(rowFr, run.to) / rowTotal) * 100}%`,
              left: `${leftPct}%`,
              // 9px to grab, 1px to see — the seam reads as a hairline, not a band.
              width: 9,
              transform: "translateX(-50%)",
              cursor: "col-resize",
              zIndex: 5,
              touchAction: "none",
              display: "flex",
              justifyContent: "center",
            }}
            title="Drag to resize columns"
          >
            <div
              style={{
                height: "100%",
                width: lit ? 2 : 1,
                background: lit ? "var(--qf-accent)" : "var(--qf-border)",
                transition: "background 120ms, width 120ms",
              }}
            />
          </div>
        ));
      })}

      {/* Row dividers (between interior rows). */}
      {rowFr.slice(0, -1).flatMap((_, i) => {
        const topPct = (sum(rowFr, i + 1) / rowTotal) * 100;
        const key = `row-${i}`;
        const lit = hovered === key || dragging === key;
        return seamRuns(rowCells, i, colFr.length).map((run) => (
          <div
            key={`${key}-${run.from}`}
            onPointerDown={(e) => startDrag(e, "row", i)}
            onPointerEnter={() => setHovered(key)}
            onPointerLeave={() => setHovered((h) => (h === key ? null : h))}
            style={{
              position: "absolute",
              left: `${(sum(colFr, run.from) / colTotal) * 100}%`,
              right: `${100 - (sum(colFr, run.to) / colTotal) * 100}%`,
              top: `${topPct}%`,
              height: 9,
              transform: "translateY(-50%)",
              cursor: "row-resize",
              zIndex: 5,
              touchAction: "none",
              display: "flex",
              alignItems: "center",
            }}
            title="Drag to resize rows"
          >
            <div
              style={{
                width: "100%",
                height: lit ? 2 : 1,
                background: lit ? "var(--qf-accent)" : "var(--qf-border)",
                transition: "background 120ms, height 120ms",
              }}
            />
          </div>
        ));
      })}

      {/* Toolbar: fixed width, one edge on the divider, so it travels with the
          drag rather than sitting at a fraction of the container. Its rows are
          the chart region's beside a screener on the left or right; beside one
          docked above or below it runs the whole grid in two parts, each key
          beside the panel it acts on (owner, 2026-10-07). */}
      {toolbar && (
        <div
          style={{
            position: "absolute",
            top: `${(sum(rowFr, toolbar.rows[0]) / rowTotal) * 100}%`,
            bottom: `${100 - (sum(rowFr, toolbar.rows[1]) / rowTotal) * 100}%`,
            left:
              toolbar.side === "right"
                ? `calc(${toolbarEdgePct}% - ${toolbarWidth}px)`
                : `${toolbarEdgePct}%`,
            width: toolbarWidth,
            // Above the pane dividers inside a section (z 6). The section cell
            // is not a stacking context, so those compete with this directly.
            zIndex: 7,
            // In parts, each part lays out its own keys (`STRIP_PART`) and this
            // is only the strip's background, rule and corners.
            ...(toolbar.parts || toolbar.pinned ? {} : STRIP_PART),
            // **No `overflow` here, and it has to stay that way.** Setting
            // `overflow-y` makes `overflow-x` compute to `auto` as well, and
            // everything this strip opens — the saved-filter popover, the four
            // annotation popouts — is absolutely positioned *sideways* out of a
            // 34px column. Scrolling the strip would clip every one of them.
            //
            // So the halves are what must not shrink (see `STRIP_HALF`), and a
            // chart too short for both simply spills past the strip. Ugly, and
            // still clickable, which is the right way round.
            background: "var(--qf-surface-alt)",
            // The rule goes on the side facing away from the charts, so the
            // strip always reads as attached to the chart it drives.
            // Rounded on the same side as the rule, and square against the
            // charts. The strip is drawn *over* the chart cell's edge, so
            // rounding the side it covers would show the cell's own corner
            // through the gap and give the seam two radii; leaving that side
            // square is what keeps it reading as attached to the chart it
            // drives, which is the same thing the rule is doing.
            ...(toolbar.side === "right"
              ? {
                  borderLeft: "1px solid var(--qf-border)",
                  borderTopLeftRadius: PANEL_RADIUS,
                  borderBottomLeftRadius: PANEL_RADIUS,
                }
              : {
                  borderRight: "1px solid var(--qf-border)",
                  borderTopRightRadius: PANEL_RADIUS,
                  borderBottomRightRadius: PANEL_RADIUS,
                }),
          }}
        >
          {(() => {
            const keys = (
              <>
                {toolbar.parts
            ? toolbar.parts.map((part) => {
                // Placed from the same row shares as the grid's own tracks and
                // its row dividers, re-read on every render — and a row drag
                // re-renders per move — so the line where one part meets the
                // next is the divider, mid-drag included. Nothing is measured,
                // so there is nothing to go stale.
                const stripFr = sum(rowFr, toolbar.rows[1]) - sum(rowFr, toolbar.rows[0]);
                const from = sum(rowFr, part.rows[0]) - sum(rowFr, toolbar.rows[0]);
                const to = sum(rowFr, part.rows[1]) - sum(rowFr, toolbar.rows[0]);
                return (
                  <div
                    key={part.rows.join("-")}
                    data-strip-part={part.rows.join("-")}
                    style={{
                      ...STRIP_PART,
                      position: "absolute",
                      left: 0,
                      right: 0,
                      top: `${(from / stripFr) * 100}%`,
                      bottom: `${100 - (to / stripFr) * 100}%`,
                      // A rule where one part meets the one above it, on the
                      // row seam: the keys below it act on a different panel.
                      ...(part.rows[0] > toolbar.rows[0]
                        ? { borderTop: "1px solid var(--qf-border)" }
                        : {}),
                    }}
                  >
                    {part.children}
                  </div>
                );
              })
            : toolbar.children}
              </>
            );
            if (!toolbar.pinned) return keys;
            // Pinned: the keys keep their own column on the side away from the
            // charts and the panel fills the rest, so it sits where its popover
            // opened — between the keys and the charts.
            const keysSide = toolbar.side === "right" ? "right" : "left";
            const panelSide = keysSide === "right" ? "left" : "right";
            return (
              <>
                <div
                  style={{
                    ...(toolbar.parts ? {} : STRIP_PART),
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    [keysSide]: 0,
                    width: toolbar.width,
                  }}
                >
                  {keys}
                </div>
                <div
                  data-strip-pinned=""
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    [panelSide]: 0,
                    width: toolbar.pinned.width,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    overflow: "hidden",
                    // The rule between the panel and the keys.
                    [keysSide === "right" ? "borderRight" : "borderLeft"]: "1px solid var(--qf-border)",
                  }}
                >
                  {toolbar.pinned.children}
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
    </MacroCursorProvider>
  );
}

/** How the strip, or each part of a split strip, stacks its keys. */
const STRIP_PART: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.3rem",
  padding: "0.35rem 0",
};

/**
 * One cell of the grid: it places a section, and pressing it makes that section
 * the active one.
 *
 * **The activation listener is a native one on this element, not a React prop,
 * and that is the whole reason this component exists.** React 17+ attaches every
 * synthetic handler to the root container and replays events from there, so even
 * `onMouseDownCapture` runs only if the event reaches the root — and SciChart's
 * canvas stops the pointer events it uses for panning and zooming long before
 * that. The visible bug was exact: clicking a chart's *toolbar* selected it and
 * clicking the chart itself did nothing, so on a two-chart layout the ring could
 * not be moved by clicking the chart it names.
 *
 * A capture-phase listener bound to this element runs on the way *down* to the
 * canvas, which is the only place in the tree that is true. Deliberately the
 * only activation path: keeping the React handler as well would leave two
 * routes to one piece of state that fire on different events, and the day they
 * disagree is the day the ring and the toolbar target different charts.
 *
 * `pointerdown` rather than `click` for the reason the old handler used
 * `mousedown` — press, not release, so the section you start a drag in is the
 * one you are working in. Its own component because a ref and an effect are
 * hooks, and hooks cannot be called from inside `sections.map`.
 */
/**
 * The bottom edge of a stacked Macro section (owner, 2026-11-01): drag it to
 * make the section's charts taller or shorter, double-click for the default.
 * The scale follows the pointer step-free (`onLive`) and is written once on
 * release (`onCommit`), rounded there to 0.05. The ratio is taken against the
 * whole section's height at the press, so headers that do not scale make the
 * edge lag the pointer slightly — the scale is what is stored, not pixels.
 */
function MacroResizeHandle({
  title,
  scale,
  onLive,
  onCommit,
}: {
  title: string;
  scale: number;
  onLive: (scale: number | null) => void;
  onCommit: (scale: number) => void;
}) {
  const [lit, setLit] = useState(false);
  const [active, setActive] = useState(false);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const cell = e.currentTarget.parentElement;
    const startHeight = cell?.getBoundingClientRect().height ?? 0;
    const startY = e.clientY;
    let last = scale;
    setActive(true);
    const onMove = (ev: PointerEvent) => {
      last = dragMacroScale(scale, startHeight, ev.clientY - startY);
      onLive(last);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setActive(false);
      onLive(null);
      if (last !== scale) onCommit(last);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      aria-label={`Resize ${title}`}
      aria-valuenow={Math.round(scale * 100) / 100}
      title="Drag to resize · double-click to reset"
      data-macro-resize=""
      onPointerDown={onPointerDown}
      onDoubleClick={() => {
        if (scale !== 1) onCommit(1);
      }}
      onPointerEnter={() => setLit(true)}
      onPointerLeave={() => setLit(false)}
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: 8,
        cursor: "ns-resize",
        zIndex: 3,
        display: "flex",
        alignItems: "flex-end",
        touchAction: "none",
      }}
    >
      <div
        style={{
          width: "100%",
          height: 3,
          borderRadius: 2,
          background: lit || active ? "var(--qf-accent)" : "transparent",
        }}
      />
    </div>
  );
}

function SectionCell({
  sectionId,
  onActivate,
  ring,
  style,
  children,
}: {
  sectionId: string;
  onActivate?: (sectionId: string) => void;
  /** The selected-chart border, or nothing. See `lib/active-section`. */
  ring?: { border: string };
  style: React.CSSProperties;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cell = ref.current;
    if (!cell || !onActivate) return;
    const activate = () => onActivate(sectionId);
    cell.addEventListener("pointerdown", activate, { capture: true });
    return () => cell.removeEventListener("pointerdown", activate, { capture: true });
  }, [sectionId, onActivate]);

  return (
    // `relative` only so the ring has something to be absolute against. Safe for
    // the screener too: `ScreenerPanel` already establishes its own positioning
    // context for the column-picker popover, so nothing inside a section was
    // reaching past the cell to the grid.
    <div ref={ref} style={{ ...style, position: "relative" }}>
      {children}
      {/* The ring is a sibling laid over the section rather than a border on the
          cell, for two independent reasons. It must not change the cell's size —
          a border would resize the WebGL canvas on every selection change — and
          it has to be *visible over a canvas*, which the two properties that
          cost no layout (an inset shadow, an outline) are not: both paint under
          composited child content, and the old 1px inset ring was invisible
          across the whole chart. A positioned overlay paints above it.
          `pointer-events: none` so it cannot eat the click that selects. */}
      {ring && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            // Above the chart, below the toolbar strip (7) and the dividers (5),
            // so the strip still draws over the edge it overlaps rather than the
            // ring cutting across it.
            zIndex: 1,
            pointerEvents: "none",
            // The same corner as the cell underneath. A square ring over a
            // rounded panel clips its own corners against nothing and reads as
            // a second, misaligned edge.
            borderRadius: PANEL_RADIUS,
            ...ring,
          }}
        />
      )}
    </div>
  );
}

/** Dispatch a section to its renderer by `kind`. */
function SectionView({
  section,
  layoutId,
  windowLink,
  crosshairLink,
  onScreenerChange,
  onSectionConfig,
  onMacroSectionConfig,
  chartJump,
  briefMember,
  sourceVersion,
  onPaneHeights,
  onPaneOrder,
  eventsFor,
  onPip,
  pipDrawn,
  onChartWindow,
  goTo,
  drawing,
  onAnnotationDrawn,
  journal,
  onLevelMoved,
  onLevelMenu,
  legendControlsFor,
  onPlacing,
  onAnnotationsDeleted,
  onAnnotationStyled,
  onPlanDrawn,
  clearNonce,
  onLastPrice,
  onScreenerSchema,
  onSelectSymbol,
  onHideScreener,
  onNewScreen,
  onEditScreen,
  screenerSide,
  dock,
  chartHeader,
  dataView,
}: {
  /** A `dataView` cell, drawn by the grid's caller. */
  dataView?: React.ReactNode;
  /** Which side the screener is docked on — a TopDown's `auto` layout reads it. */
  screenerSide?: "left" | "right" | "top" | "bottom";
  section: Section;
  layoutId: string;
  /** The layout's "Date range" link key, when it is on. */
  windowLink?: string;
  /** The layout's crosshair link key, when it is on (it defaults on). */
  crosshairLink?: string;
  onScreenerChange?: (sectionId: string, patch: ScreenerPatch) => void;
  onSectionConfig?: (sectionId: string, config: BriefBoardConfig) => void;
  onMacroSectionConfig?: (sectionId: string, config: MacroSectionConfig) => void;
  chartJump?: ChartJump;
  /** A Daily Brief's edits go to the member's own board, not the preset. */
  briefMember?: boolean;
  /** The copied layout's published version — what a member's board is based on. */
  sourceVersion?: number;
  /** Pane heights settled after a chart divider drag. */
  onPaneHeights?: (chartGroupId: string, heights: string[]) => void;
  /** A chart's panes were restacked, arriving as the finished array. */
  onPaneOrder?: (chartGroupId: string, panes: Pane[]) => void;
  /** Which company events each chart marks. View state today (D8). */
  eventsFor?: (chartGroupId: string) => { earnings: boolean; news: boolean };
  /** One edit to a chart's picture-in-picture panel. */
  onPip?: (chartGroupId: string, patch: PipPatch) => void;
  /** Whether this chart should draw its PIP — the two-chart rule. */
  pipDrawn?: (chartGroupId: string) => boolean;
  onChartWindow?: (
    chartGroupId: string,
    window: { visibleBars?: number; barsRange?: number },
  ) => void;
  goTo?: { chartGroupId: string; date: string; nonce: number };
  /**
   * The armed drawing tool, and which chart it applies to.
   *
   * One palette serves whichever chart is active, so the tool is handed only to
   * that chart — arming a trendline and clicking a different chart must not draw
   * on it, which is what passing it to every chart would do.
   */
  drawing?: { chartGroupId: string; tool: AnnotationTool | null; trading?: string | null };
  onAnnotationDrawn?: (chartGroupId: string, paneId: string, annotation: Annotation) => void;
  /** The journal's lines, and the one chart whose symbol they belong to. */
  journal?: {
    chartGroupId: string;
    annotations: readonly Annotation[];
    editable?: ReadonlySet<string>;
  };
  onLevelMoved?: (annotationId: string, price: number) => void;
  onLevelMenu?: (annotationId: string, x: number, y: number) => void;
  /**
   * The value legend's controls for one chart.
   *
   * A function of the group rather than a value, because every one of them
   * addresses a plot in *that* chart — a single object would give the second
   * chart on a layout the first one's rows.
   */
  legendControlsFor?: (group: ChartGroup) => LegendControls | undefined;
  onPlacing?: (placing: boolean) => void;
  onAnnotationsDeleted?: (chartGroupId: string, annotationIds: readonly string[]) => void;
  onAnnotationStyled?: (
    chartGroupId: string,
    annotationId: string,
    style: NonNullable<Annotation["style"]>,
  ) => void;
  /** Bumped to clear the drawings the contract cannot reach. */
  onPlanDrawn?: (chartGroupId: string, entry: number, stop: number, at: number) => void;
  clearNonce?: number;
  onLastPrice?: (symbol: string, price: number, at: number) => void;
  onScreenerSchema?: (sectionId: string, schema: ScreenerSchema) => void;
  onSelectSymbol?: (symbol: string) => void;
  onHideScreener?: () => void;
  onNewScreen?: () => void;
  onEditScreen?: (via?: "columns") => void;
  dock?: DockView;
  chartHeader?: React.ReactNode;
}) {
  const group = chartGroupConfig(section);
  if (group)
    return (
      <div style={chartCell}>
        {chartHeader}
        {/* The chart takes what the header leaves. `minHeight: 0` because a flex
            child defaults to its content's minimum, and a WebGL canvas has none
            — without it the chart would refuse to shrink below its last size. */}
        <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
          <ChartGroupView
            group={group}
            layoutId={layoutId}
            windowLink={windowLink}
            crosshairLink={crosshairLink}
            onPaneHeights={
              onPaneHeights ? (heights) => onPaneHeights(group.id, heights) : undefined
            }
            onPaneOrder={onPaneOrder ? (panes) => onPaneOrder(group.id, panes) : undefined}
            events={eventsFor?.(group.id)}
            onWindow={onChartWindow ? (window) => onChartWindow(group.id, window) : undefined}
            onPip={onPip ? (patch) => onPip(group.id, patch) : undefined}
            pipHidden={pipDrawn ? !pipDrawn(group.id) : false}
            // Only the chart the box was typed into: one request names one
            // chart, and handing it to every chart would move charts nobody
            // touched on a synced-looking layout that is not synced.
            goTo={goTo?.chartGroupId === group.id ? goTo : undefined}
            drawingTool={drawing?.chartGroupId === group.id ? drawing.tool : null}
            tradingTool={drawing?.chartGroupId === group.id ? (drawing.trading ?? null) : null}
            // Only the chart whose symbol the lines were derived for. Handing
            // them to every chart would draw AAPL's stop across a TSLA chart.
            journalAnnotations={
              journal?.chartGroupId === group.id ? journal.annotations : undefined
            }
            journalEditable={
              journal?.chartGroupId === group.id ? journal.editable : undefined
            }
            onLevelMoved={onLevelMoved}
            onLevelMenu={onLevelMenu}
            legendControls={legendControlsFor?.(group)}
            onPlacing={onPlacing}
            onAnnotationsDeleted={
              onAnnotationsDeleted
                ? (ids) => onAnnotationsDeleted(group.id, ids)
                : undefined
            }
            onAnnotationStyled={
              onAnnotationStyled
                ? (id, style) => onAnnotationStyled(group.id, id, style)
                : undefined
            }
            onPlanDrawn={
              onPlanDrawn
                ? (entry, stop, at) => onPlanDrawn(group.id, entry, stop, at)
                : undefined
            }
            clearNonce={clearNonce}
            onLastPrice={onLastPrice}
            onAnnotationDrawn={
              onAnnotationDrawn
                ? (paneId, annotation) => onAnnotationDrawn(group.id, paneId, annotation)
                : undefined
            }
          />
        </div>
      </div>
    );

  const screener = screenerConfig(section);
  if (screener) {
    // An element, not a mount: it is rendered only if the dock lists the
    // screener. A panel that is not showing is **unmounted**, never hidden —
    // hiding would keep the grid's socket open and its rows updating behind a
    // panel nobody is reading, and give AG Grid a zero-height container to
    // measure, which it answers by rendering no rows and never recovering.
    const screenerView = screener.topDown ? (
      // A TopDown screen (`docs/features/topdown.md`): three grids, the third
      // an ordinary screener over the screen it references.
      <TopDownView
        config={{ ...screener, topDown: screener.topDown }}
        side={screenerSide}
        onSelectSymbol={onSelectSymbol}
        onHide={onHideScreener}
        onNewScreen={onNewScreen}
        onEditScreen={onEditScreen}
        onConfigChange={
          onScreenerChange ? (patch) => onScreenerChange(section.id, patch) : undefined
        }
      />
    ) : screener.seasonality ? (
      // A Seasonality screen (`docs/features/seasonality.md`): windows over the
      // base screen's symbols, under that screen's own bar.
      <SeasonalityView
        config={{ ...screener, seasonality: screener.seasonality }}
        side={screenerSide}
        onSelectSymbol={onSelectSymbol}
        onHide={onHideScreener}
        onNewScreen={onNewScreen}
        onEditScreen={onEditScreen}
        onConfigChange={
          onScreenerChange ? (patch) => onScreenerChange(section.id, patch) : undefined
        }
      />
    ) : (
      <ScreenerView
        config={screener}
        onSelectSymbol={onSelectSymbol}
        onHide={onHideScreener}
        onNewScreen={onNewScreen}
        onEditScreen={onEditScreen}
        dockSide={screenerSide}
        onConfigChange={
          onScreenerChange ? (patch) => onScreenerChange(section.id, patch) : undefined
        }
        onSavedFiltersChange={
          onScreenerChange
            ? (savedFilters) => onScreenerChange(section.id, { savedFilters })
            : undefined
        }
        onSchema={
          onScreenerSchema ? (schema) => onScreenerSchema(section.id, schema) : undefined
        }
      />
    );
    if (!dock) return screenerView;
    const panels = dock.panels.map((panel) => (
      <Fragment key={panel}>{panel === "screener" ? screenerView : dock.nodes[panel]}</Fragment>
    ));
    return (
      <DockCell
        panels={panels}
        sizes={dock.sizes}
        direction={dock.direction}
        onSizes={dock.onSizes}
        onSwap={dock.onSwap}
      />
    );
  }

  if (section.kind === "dataView" && dataView) return <>{dataView}</>;

  const buckets = bucketsBriefConfig(section);
  if (buckets) return <BucketsBriefView config={buckets} />;

  const board = briefBoardConfig(section);
  if (board)
    return (
      <BriefBoardView
        config={board}
        member={!!briefMember}
        sourceVersion={sourceVersion}
        onChange={onSectionConfig ? (next) => onSectionConfig(section.id, next) : undefined}
        onOpenSymbol={onSelectSymbol}
      />
    );

  if (isMacroKind(section.kind))
    return (
      <MacroSectionHost
        section={section as Section & { kind: MacroKind }}
        onConfig={
          onMacroSectionConfig ? (next) => onMacroSectionConfig(section.id, next) : undefined
        }
        chartJump={chartJump}
      />
    );

  return <PlaceholderPanel kind={section.kind} title={section.title} />;
}

const KIND_BLURB: Record<Section["kind"], string> = {
  chartGroup: "Chart",
  screener: "Screener",
  blotter: "Order blotter",
  heatmap: "Heatmap",
  news: "News feed",
  bucketsBrief: "Buckets Brief",
  briefBoard: "Daily Brief",
  dataView: "Data",
  ...MACRO_KIND_TITLE,
};

/** Typed placeholder for non-chart section kinds (real widgets land later). */
function PlaceholderPanel({ kind, title }: { kind: Section["kind"]; title?: string }) {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.35rem",
        padding: "1rem",
        textAlign: "center",
        background: "var(--qf-surface-alt)",
        color: "var(--qf-text-secondary)",
      }}
    >
      <span style={{ fontSize: "0.75rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {KIND_BLURB[kind]}
      </span>
      <strong style={{ color: "var(--qf-text)", fontSize: "1rem" }}>{title ?? KIND_BLURB[kind]}</strong>
      <span style={{ fontSize: "0.8rem" }}>{KIND_BLURB[kind]} — coming soon</span>
    </div>
  );
}

/** The chart's column: toolbar above, surface filling the rest. */
const chartCell: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
};
